import { merge } from 'lodash';
import React, { ComponentType } from 'react';
import { css, DefaultTheme, ThemeProvider } from 'styled-components';
import { confirmingStyle } from './confirmTwice';
import { ZTooltipLookContext } from '../../../../widgets/ztooltip';
import { DropdownToneContext } from '../../../../widgets/dropdown';
import { OutlineButton } from '../../../../widgets/buttons';
import { tint } from '../../../../widgets/buttons/buttonStyle';

// I COLORI DEI DUE EDITOR, IN UN POSTO SOLO (prototipo V3, AGGIORNAMENTI §1; utente, 2026-10-19).
//
// Scene Editor e Composer sono due ambienti della stessa piattaforma e leggono la stessa gerarchia:
// cornice < fondo dell'Inspector < pannello = card, con ciò che galleggia (menu, modali) sul bianco.
// I valori sono quelli chiari del prototipo (Neutral v2); il testo «faint» e il «chapter» sono un
// passo più scuri, per reggere 4.5:1 e 3:1 sul fondo dell'Inspector.
//
// SOLO DENTRO GLI EDITOR: il tema del Backoffice resta com'è per tutte le altre pagine. Qui si
// sovrascrivono i suoi grigi (`editorTheme`), così i componenti che li leggono dal tema — il pannello
// delle proprietà, i pannelli del workspace, il rail — prendono questi senza saperlo.
//
// L'ARANCIO SEGUE IL MARCHIO: è `theme.app.secondary`, e le sue varianti (testo, lavato, filo, scuro)
// si ricavano da lui, così un tema white-label le cambia tutte insieme.
export const EDITOR_COLORS = {
    chrome: '#dbdde1',
    sunken: '#e9ebed',
    panel: '#f9fafb',
    card: '#f9fafb',
    raised: '#ffffff',
    nest: '#edeff1',
    field: '#f1f2f4',
    track: '#e3e5e8',
    hover: '#e3e5e8',
    rowHover: '#f1f2f4',
    line: '#d1d3d8',
    lineSoft: '#e0e2e5',
    fg: '#262a30',
    muted: '#55595f',
    faint: '#676a6e',
    chapter: '#84878c'
};

/**
 * Il tema del Backoffice con i grigi degli editor al posto dei suoi, e I BOTTONI CONDIVISI NELLE RICETTE DEGLI EDITOR
 * (utente, 2026-10-20, `docs/mappatura-bottoni-editor.md`): il Primary diventa l'azione principale tenue (lavato, filo e
 * parola arancio scuro), il Basic diventa il secondario neutro (filo grigio, niente arancio), i bottoni testo l'arancio
 * da testo a peso medio. L'arancio è quello del marchio, schiarito o scurito, così un tema white-label li segue.
 */
// IL PRIMARIO PIENO DEGLI EDITOR (utente, 2026-10-21): Save in testata e l'azione delle finestre di domanda. È l'arancio
// del marchio scurito (84%, l'accent-deep), testo bianco: chiaramente il primario, ma meno acceso del #E8650A dello
// Storybook, che accanto ai bottoni tenui stonava. Al passaggio un passo più scuro (76%).
export const editorPrimaryFill = (accent: string) => ({ background: tint(accent, -16), hover: tint(accent, -24) });

// Il tema delle finestre di domanda: i bottoni di Zakeke (Primary e Basic), col Primary nel pieno scuro qui sopra.
export const questionDialogTheme = (theme?: DefaultTheme): DefaultTheme => {
    const fill = editorPrimaryFill(theme?.app?.secondary ?? '#e8650a');
    const solid = (background: string) => ({ backgroundColor: background, textColor: '#FFFFFF' });
    return merge({}, theme, {
        app: {
            component: {
                button: {
                    primary: {
                        border: `1px solid ${fill.background}`,
                        default: solid(fill.background),
                        hover: solid(fill.hover),
                        active: solid(fill.hover),
                        disabled: { ...solid(fill.background), border: `1px solid ${fill.background}` }
                    }
                }
            }
        }
    });
};

export const editorTheme = (theme?: DefaultTheme): DefaultTheme => {
    const accent = theme?.app?.secondary ?? '#e8650a';
    const wash = `color-mix(in srgb, ${accent} 8%, #ffffff)`;
    const washHover = `color-mix(in srgb, ${accent} 14%, #ffffff)`;
    const accentLine = `1px solid color-mix(in srgb, ${accent} 38%, ${EDITOR_COLORS.lineSoft})`;
    const accentText = `color-mix(in srgb, ${accent} 72%, #000000)`;
    const neutralLine = `1px solid ${EDITOR_COLORS.line}`;
    return merge({}, theme, {
        app: {
            pageBackgroundColor: EDITOR_COLORS.sunken,
            backgroundToolbar: EDITOR_COLORS.field,
            borderColor: EDITOR_COLORS.line,
            textColor: EDITOR_COLORS.fg,
            primary: EDITOR_COLORS.muted,
            component: {
                pageBlocksContainer: { pageBlock: { backgroundColor: EDITOR_COLORS.panel } },
                button: {
                    primary: {
                        border: accentLine,
                        default: { backgroundColor: wash, textColor: accentText },
                        hover: { backgroundColor: washHover, textColor: accentText },
                        active: { backgroundColor: washHover, textColor: accentText },
                        disabled: { backgroundColor: wash, textColor: accentText, border: accentLine }
                    },
                    basic: {
                        border: neutralLine,
                        default: { backgroundColor: EDITOR_COLORS.raised, textColor: EDITOR_COLORS.fg },
                        hover: { backgroundColor: EDITOR_COLORS.hover, textColor: EDITOR_COLORS.fg },
                        active: { backgroundColor: EDITOR_COLORS.hover, textColor: EDITOR_COLORS.fg },
                        disabled: { backgroundColor: EDITOR_COLORS.raised, textColor: EDITOR_COLORS.fg, border: neutralLine }
                    }
                },
                textButton: {
                    fontWeight: 500,
                    primary: { default: { color: accentText }, hover: { color: accent }, active: { color: accentText } }
                }
            }
        }
    });
};

// Gli editor accendono anche il loro tooltip: scuro e senza freccia, come nel prototipo (ZTooltipLookContext).
export const withEditorTheme =
    <P extends object>(Editor: ComponentType<P>) =>
    (props: P) => (
        <ThemeProvider theme={editorTheme}>
            <ZTooltipLookContext.Provider value='editor'>
                {/* I MENU DEGLI EDITOR SONO TENUI (utente, 2026-10-20): la voce scelta in arancio lavato, non pieno. */}
                <DropdownToneContext.Provider value='soft'>
                    <Editor {...props} />
                </DropdownToneContext.Provider>
            </ZTooltipLookContext.Provider>
        </ThemeProvider>
    );

// L'AZIONE PRINCIPALE MORBIDA (prototipo `.btn.tint`, `.btn.primary.rest`): lavato arancio, parola arancio scuro e
// un filo. L'arancio pieno resta agli stati e ai momenti — un interruttore acceso, la conferma —, non a un bottone
// che sta lì tutto il tempo.
export const softAccentButton = css`
    border: 1px solid var(--editor-accent-line, #e2b597);
    background: var(--editor-accent-wash, rgb(232 101 10 / 8%));
    color: var(--editor-accent-text, #b04b07);

    &:hover:not(:disabled),
    &:active:not(:disabled) {
        border-color: var(--editor-accent-line, #e2b597);
        background: var(--editor-accent-wash-hover, rgb(232 101 10 / 16%));
        color: var(--editor-accent-text, #b04b07);
    }
`;

// I CAMPI DI RICERCA DEGLI EDITOR SONO UNO (prototipo V3, `.search-wrap`): 26px, il fondo grigio dei campi, il filo
// delle linee, raggio 8 e la lente piccola e chiara. Si scrive una volta qui, sul contenitore, e vale per ogni
// `SearchInput` dentro gli editor — elenchi, parametri del materiale, menu degli slot — senza toccarli uno per uno.
// Fuori dagli editor e nelle finestre (che stanno in un portale) il campo resta quello del Backoffice. La
// specificità doppia serve a passare sopra al rientro che il campo si dà da sé.
const editorSearchField = css`
    [data-search-field][data-search-field] input {
        height: 26px;
        min-height: 26px;
        padding-left: 28px;
        border: 1px solid var(--editor-line);
        border-radius: 8px;
        background: var(--editor-field);
        color: var(--editor-fg);
        font-size: 12px;
        box-shadow: none;

        &::placeholder {
            color: var(--editor-faint);
        }

        &:focus {
            border-color: var(--editor-accent);
            outline: none;
            box-shadow: none;
        }
    }

    [data-search-field] [data-search-icon] {
        left: 9px;
        color: var(--editor-faint);

        svg {
            width: 13px;
            height: 13px;
        }
    }
`;

// LE VARIABILI, sul contenitore di ciascun editor. Le `--composer-*` e le `--panel-*` sono i nomi che
// i componenti usano già: qui diventano alias degli stessi valori, così la topbar condivisa e le card
// del pannello montate nel Composer non ricadono più sui loro ripieghi.
export const editorPalette = css`
    --editor-chrome: ${EDITOR_COLORS.chrome};
    --editor-sunken: ${EDITOR_COLORS.sunken};
    --editor-panel: ${EDITOR_COLORS.panel};
    --editor-card: ${EDITOR_COLORS.card};
    --editor-raised: ${EDITOR_COLORS.raised};
    --editor-nest: ${EDITOR_COLORS.nest};
    --editor-field: ${EDITOR_COLORS.field};
    --editor-track: ${EDITOR_COLORS.track};
    --editor-hover: ${EDITOR_COLORS.hover};
    --editor-row-hover: ${EDITOR_COLORS.rowHover};
    --editor-line: ${EDITOR_COLORS.line};
    --editor-line-soft: ${EDITOR_COLORS.lineSoft};
    --editor-fg: ${EDITOR_COLORS.fg};
    --editor-muted: ${EDITOR_COLORS.muted};
    --editor-faint: ${EDITOR_COLORS.faint};
    --editor-chapter: ${EDITOR_COLORS.chapter};
    --editor-danger: #c33a31;
    --editor-confirm-bg: #fce4e4;
    --editor-confirm-bg-hover: color-mix(in srgb, var(--editor-danger) 14%, var(--editor-confirm-bg));
    --editor-accent: ${(props) => props.theme.app.secondary};
    --editor-accent-deep: color-mix(in srgb, var(--editor-accent) 84%, #000);
    --editor-accent-text: color-mix(in srgb, var(--editor-accent) 76%, #000);
    --editor-accent-wash: color-mix(in srgb, var(--editor-accent) 8%, transparent);
    --editor-accent-line: color-mix(in srgb, var(--editor-accent) 38%, ${EDITOR_COLORS.lineSoft});
    --editor-accent-wash-hover: color-mix(in srgb, var(--editor-accent) 16%, transparent);

    --composer-app: var(--editor-chrome);
    --composer-panel: var(--editor-panel);
    --composer-sunken: var(--editor-sunken);
    --composer-hover: var(--editor-row-hover);
    --composer-line: var(--editor-line);
    --composer-line-soft: var(--editor-line-soft);
    --composer-fg: var(--editor-fg);
    --composer-muted: var(--editor-muted);
    --composer-faint: var(--editor-faint);
    --composer-accent: var(--editor-accent);
    --composer-accent-deep: var(--editor-accent-deep);
    --composer-accent-text: var(--editor-accent-text);
    --composer-accent-wash: var(--editor-accent-wash);

    --panel-ground: var(--editor-sunken);
    --panel-surface: var(--editor-card);
    --panel-line: var(--editor-line);
    --panel-ink: var(--editor-fg);
    --panel-ink-muted: var(--editor-muted);
    --panel-field: var(--editor-field);
    --panel-accent: var(--editor-accent);

    ${confirmingStyle}
    ${editorSearchField}

    /* IL FUOCO DA TASTIERA È ARANCIO, come nel prototipo (utente, 2026-10-20): il contorno di sistema è blu. Vale
       solo dove un componente non ne disegna uno suo (il selettore :where non pesa), e solo da tastiera. */
    /* UN BOTTONE BIANCO AL PASSAGGIO SI RIEMPIE DI GRIGIO (prototipo, .btn:hover; utente 2026-10-21): in testata e nei
       comandi in colonna. Nello Storybook l'Outline accende il filo d'arancio; qui il filo resta grigio e cambia il fondo. */
    ${OutlineButton}:hover:not([disabled]) {
        border-color: var(--editor-line);
        background-color: var(--editor-hover);
    }

    ${OutlineButton}:active:not([disabled]) {
        border-color: var(--editor-line);
        background-color: var(--editor-track);
    }

    :where(*:focus-visible) {
        outline: 2px solid var(--editor-accent);
        outline-offset: 1px;
    }

    /* OGNI COMANDO RAGGIUNTO COL TAB SI VEDE (utente, 2026-10-20): bottoni, tab, voci di menu e scelte. Anche dove un
       componente toglieva il contorno o ne disegnava uno blu, qui vince l'arancio. I campi di testo no: lì il fuoco lo
       dice già il bordo. */
    :is(button, a[href], summary, [role='button'], [role='tab'], [role='menuitem'], [role='option'], [role='radio'], [role='treeitem']):focus-visible {
        outline: 2px solid var(--editor-accent) !important;
        outline-offset: 1px;
    }
`;
